import { AppEnvironment } from '@/constants/appEnvironment';
import { axleEnvironment } from '@/utils/axleEnvironment';
import { envVars } from '@/utils/envVars';
import { logMessage } from '@/utils/tracking';

// An environment without an app ID configured never loads the widget.
export const isPylonEnabledForEnvironment = (): boolean => {
    if (!envVars.pylonAppId) {
        return false;
    }
    return axleEnvironment !== AppEnvironment.LOCAL || envVars.pylonInLocal;
};

const WIDGET_SRC_ORIGIN = 'https://widget.usepylon.com/widget';

// Pylon's widget accepts many more commands than these.
// Without deifinitions handed to us, we can justnarrow to the ones we send.

type PylonChatSettings = {
    app_id: string;
    email: string;
    name: string;
    email_hash: string;
};

export enum PylonCommand {
    ShowChatBubble = 'showChatBubble',
    HideChatBubble = 'hideChatBubble',
}

type PylonGlobal = (command: PylonCommand) => void;

declare global {
    interface Window {
        pylon?: { chat_settings: PylonChatSettings };
        Pylon?: PylonGlobal;
    }
}

const SCRIPT_ID = 'pylon-widget-script-lookup';

const callPylon = (command: PylonCommand): void => {
    try {
        window.Pylon?.(command);
    } catch {
        logMessage(`Pylon chat widget rejected ${command}`);
    }
};

export const loadPylonWidget = (settings: PylonChatSettings): (() => void) => {
    window.pylon = { chat_settings: settings };

    // The script cannot be unloaded, so teardown hides the launcher and drops the identity
    //  it would otherwise keep answering with.
    const hide = () => {
        callPylon(PylonCommand.HideChatBubble);
        delete window.pylon;
    };

    if (document.getElementById(SCRIPT_ID)) {
        callPylon(PylonCommand.ShowChatBubble);
        return hide;
    }

    // Pylon's widget renders its launcher on boot without being asked
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${WIDGET_SRC_ORIGIN}/${settings.app_id}`;
    script.onerror = () => logMessage('Pylon chat widget script failed to load');
    document.head.appendChild(script);

    return hide;
};
